import { zodResolver } from '@hookform/resolvers/zod'
import {
  useForm,
  type FieldValues,
  type Resolver,
  type UseFormProps,
  type UseFormReturn,
} from 'react-hook-form'
import type { z } from 'zod'

/**
 * `useForm` with this app's validation timing baked in.
 *
 * `onTouched` waits for the first blur before it says anything, so a field
 * never turns red while the user is still typing into it for the first time.
 * `reValidateMode: 'onChange'` then flips the other way: once a field is known
 * to be wrong, the message clears the moment it is fixed, instead of making
 * the user blur again to find out.
 *
 * `shouldFocusError` moves focus to the first invalid field on a blocked
 * submit, which is what keeps a long form usable from the keyboard.
 */
export function useValidatedForm<TFieldValues extends FieldValues>(
  schema: z.ZodType<TFieldValues, TFieldValues>,
  options: Omit<
    UseFormProps<TFieldValues, unknown, TFieldValues>,
    'resolver' | 'mode' | 'reValidateMode' | 'shouldFocusError'
  > = {},
): UseFormReturn<TFieldValues> {
  return useForm<TFieldValues, unknown, TFieldValues>({
    // Inside a generic wrapper the resolver widens to the constraint
    // (`FieldValues`) and loses the schema's own type. The schema and
    // `TFieldValues` are the same type by construction at every call site,
    // which is what this restates for the compiler.
    resolver: zodResolver(schema) as Resolver<TFieldValues, unknown, TFieldValues>,
    mode: 'onTouched',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    ...options,
  })
}
